import { useAtom } from "jotai";
import { useEffect, useRef } from "react";
import { PiUserLight } from "react-icons/pi";
import { Tooltip, TooltipRefProps } from "react-tooltip";
import { isMenuOpenAtom, isMobile } from "../../stores/Navigation";
import { getEmbeddedSamplePath, getV1Path } from "../../views/App/urls";
import { SampleAnchor } from "../PaperShared/Sample";
import "./Intro.scss";
function Intro() {
  const modelId = "mini";
  const sampleId = "2032000";
  const embeddedSamplePath = `#${getEmbeddedSamplePath(modelId, sampleId)}`;
  const [isMenuOpen, setIsMenuOpen] = useAtom(isMenuOpenAtom);
  const tooltipRef = useRef<TooltipRefProps>(null);
  const tooltipPosition = isMobile() ? undefined : { x: 275, y: 300 };

  // Show the menu before showing the app tooltip.
  const openAppHandler = (e: React.MouseEvent) => {
    const delay = isMenuOpen ? 0 : 300;
    setIsMenuOpen(true);
    setTimeout(() => {
      tooltipRef.current?.open();
    }, delay);
    e.preventDefault();
  };

  // Auto-close the app tooltip after a delay.
  const tooltipShownHandler = () => {
    setTimeout(() => {
      tooltipRef.current?.close();
    }, 8000);
  };

  // Close the app tooltip when the menu is closed.
  useEffect(() => {
    if (!isMenuOpen) {
      tooltipRef.current?.close();
    }
  }, [isMenuOpen]);

  return (
    <section id="IntroV2">
      <h1>
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="logo" />
        <span>GPT-2 Circuits</span>
      </h1>
      <p className="authors">
        <a className="author" href="mailto:peterjlai@gmail.com">
          <PiUserLight className="icon" />
          <span>Peter Lai</span>
        </a>
        <span className="author">
          <PiUserLight className="icon" />
          <span>Winston Huang</span>
        </span>
      </p>
      <p>
        We've created an{" "}
        <a href="#/" onClick={openAppHandler}>
          app
        </a>{" "}
        that extracts “circuits” from an LLM using the GPT-2 architecture. The features that we
        discover within these circuits fire upon encountering specific sequences of tokens, which
        then act in “ensemble” to produce higher level features. These higher level features
        activate in response to (i) specific grammatical patterns and (ii) topics. What results is a
        graph tying the effects of specific input tokens to the probabilities of the next token in a
        sequence.
      </p>
      <figure>
        <figcaption>
          <SampleAnchor
            modelId={modelId}
            sampleId={sampleId}
            text="Predicting What Belongs in a List With 100 Birds"
          />
        </figcaption>
        <iframe title="Embedded Circuit" src={embeddedSamplePath} height="420px" />
      </figure>
      <p>
        This research builds upon a <a href={`#${getV1Path()}`}>previous version</a> of this project
        in which we extract circuits from 4 &amp; 6 layer character-based GPT-2 models.
      </p>
      <Tooltip
        ref={tooltipRef}
        anchorSelect="menu"
        openEvents={{}}
        closeEvents={{}}
        afterShow={tooltipShownHandler}
        position={tooltipPosition}
        place="right"
      >
        Select a sample to view a circuit.
      </Tooltip>
    </section>
  );
}

export { Intro };
